/**
 * The one way an instant becomes text: a tenant locale and operating time zone,
 * each overridable by the person reading.
 *
 * Bare `toLocaleString` follows the browser, so the same event read as
 * `7/25/2026, 2:00:00 AM` in one table and `Jul 25, 2026, 02:00 AM` in another,
 * and `10/1/2026` was 1 Oct or 10 Jan depending on who looked. ESLint bans the
 * bare calls in `src/`. Date-only values stay `PlainDate` and go
 * through `calendar-dates`, since a day has no zone to convert to.
 *
 * @module utils/format-instant
 */

import { Temporal } from "temporal-polyfill";

import { DEFAULT_LOCALE, DEFAULT_TIME_ZONE, isValidLocale, isValidTimeZone } from "@/utils/region";

/** How long after an event relative text is still more useful than a date. */
const RELATIVE_WINDOW_DAYS = 7;

export interface Region {
  locale: string;
  timeZone: string;
}

/** What a tenant or a person has chosen; absent means defer to the next level. */
export interface RegionPreference {
  locale?: string | null;
  timeZone?: string | null;
}

/** An instant as the wire, a `Date` or a Temporal carries it. */
export type InstantInput = string | number | Date | Temporal.Instant;

/**
 * The region in force: the person's choice, else the tenant's, else the
 * defaults. A value the runtime cannot use is skipped rather than thrown on,
 * so a stale stored value never blanks every date on the page.
 */
export function resolveRegion(
  tenant: RegionPreference | undefined,
  user: RegionPreference | undefined,
): Region {
  return {
    locale: [user?.locale, tenant?.locale].find((v) => v && isValidLocale(v)) ?? DEFAULT_LOCALE,
    timeZone:
      [user?.timeZone, tenant?.timeZone].find((v) => v && isValidTimeZone(v)) ?? DEFAULT_TIME_ZONE,
  };
}

export const DEFAULT_REGION: Region = { locale: DEFAULT_LOCALE, timeZone: DEFAULT_TIME_ZONE };

let current: Region = DEFAULT_REGION;

/** The region every formatter below reads. */
export function getRegion(): Region {
  return current;
}

/**
 * Sets the region the formatters read.
 *
 * A write and nothing more: the caller re-renders what reads it, which the
 * workspace does by remounting its content when the region changes.
 */
export function setRegion(next: Region): void {
  current = next;
}

function toDate(value: InstantInput): Date | undefined {
  const date =
    value instanceof Temporal.Instant
      ? new Date(value.epochMilliseconds)
      : new Date(value as string | number | Date);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function format(value: InstantInput, options: Intl.DateTimeFormatOptions, region: Region): string {
  const date = toDate(value);
  if (!date) {
    return String(value);
  }
  return new Intl.DateTimeFormat(region.locale, { ...options, timeZone: region.timeZone }).format(
    date,
  );
}

const DATE: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric" };
const TIME: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };

/** `1 Oct 2026`, the day as it falls in the region's zone. */
export function formatDate(value: InstantInput, region: Region = current): string {
  return format(value, DATE, region);
}

/**
 * `1 Oct 2026, 14:05`. `zone` adds the abbreviation (`IST`), for audit detail
 * and exports where which clock is the question.
 */
export function formatDateTime(
  value: InstantInput,
  options: { zone?: boolean; region?: Region } = {},
): string {
  return format(
    value,
    options.zone ? { ...DATE, ...TIME, timeZoneName: "short" } : { ...DATE, ...TIME },
    options.region ?? current,
  );
}

const UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ["day", 86_400_000],
  ["hour", 3_600_000],
  ["minute", 60_000],
];

/**
 * `2 hours ago` for an instant inside the last seven days, the absolute date
 * and time beyond that or in the future. Feeds and notifications only: a
 * relative phrase decays, so it is never the sole record of when.
 */
export function formatRelative(
  value: InstantInput,
  options: { now?: InstantInput; region?: Region } = {},
): string {
  const region = options.region ?? current;
  const date = toDate(value);
  const now = toDate(options.now ?? Date.now());
  if (!date || !now) {
    return String(value);
  }
  const elapsed = now.getTime() - date.getTime();
  if (elapsed < 0 || elapsed > RELATIVE_WINDOW_DAYS * 86_400_000) {
    return formatDateTime(date, { region });
  }
  const formatter = new Intl.RelativeTimeFormat(region.locale, { numeric: "auto" });
  const unit = UNITS.find(([, size]) => elapsed >= size) ?? (["minute", 60_000] as const);
  return formatter.format(-Math.floor(elapsed / unit[1]), unit[0]);
}
